import React, { useEffect, useRef, useState } from 'react';
import { GENERAL_DEPARTMENT_ID, deptColor } from '@battleops/shared';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import Icon from './Icon';
import s from './DeptGuessChip.module.css';

export { useDeptDraft } from '@battleops/client/src/data/useDeptDraft';

// The inferred department on a task being captured (v0.1.242, shared/deptinfer.js): a chip in the department's colour,
// a LOW-confidence guess muted and marked GUESS; click → pick another (it teaches the learned layer on create).
export default function DeptGuessChip({ guess, onPick, showWhy = true }) {
  const { departments } = useDepartments();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  if (!guess) return null;
  const d = departments.find((x) => x.id === guess.departmentId) || departments.find((x) => x.id === GENERAL_DEPARTMENT_ID) || { name: 'General', icon: '◎' };
  const low = guess.confidence === 'low';
  const locked = guess.source === 'lock';
  return (
    <span className={s.wrap} ref={box} onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}>
      <button type="button" className={s.chip} data-guess={low || undefined} style={{ '--dc': d.color || deptColor(d) }} disabled={locked}
        onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}
        title={`${guess.why || ''}${locked ? '' : ' — click to change'}`}>
        {locked ? <Icon name="lock" size={11} /> : <span className={s.dot} />}
        <span className={s.name}>{d.icon} {d.name}</span>
        {low ? <span className={s.tag}>guess</span> : null}
        {!locked ? <Icon name="chevron-down" size={13} /> : null}
      </button>
      {showWhy && guess.why ? <span className={s.why}>{guess.why}</span> : null}
      {open ? (
        <span className={s.menu} role="listbox" aria-label="Department">
          <span className={s.menuHead}>Put it in</span>
          {departments.map((x) => {
            const on = x.id === guess.departmentId;
            return (
              <button type="button" key={x.id} role="option" aria-selected={on} className={s.option} data-on={on || undefined} style={{ '--dc': x.color || deptColor(x) }}
                onMouseDown={(e) => e.preventDefault()} onClick={() => { setOpen(false); onPick?.(x.id === GENERAL_DEPARTMENT_ID ? null : x.id); }}>
                <span className={s.swatch}>{x.icon}</span><span className={s.optName}>{x.name}</span>
                <span className={s.tick}>{on ? <Icon name="check" size={14} /> : null}</span>
              </button>
            );
          })}
        </span>
      ) : null}
    </span>
  );
}
